import { openDB } from 'idb';

const isMobile = /Mobi|Android/i.test(navigator.userAgent);

// Disk limits (much safer than heap limits)
const MAX_CACHE_BYTES = isMobile ? 20 * 1024 * 1024 : 60 * 1024 * 1024;

// IndexedDB Setup
const dbPromise = openDB('imageBlobCache', 1, {
  upgrade(db) {
    db.createObjectStore('images'); // key -> Blob
    db.createObjectStore('meta'); // key -> { size, lastUsed }
  },
});

// ObjectURL Memory Map
// (short-lived only)
const activeObjectURLs = new Map<string, string>();

// LRU GET
const getFromCache = async (key: string): Promise<string | null> => {
  // Check if an objectURL has already been created for this image
  const memUrl = activeObjectURLs.get(key);
  if (memUrl) return memUrl;

  const db = await dbPromise;
  const blob: Blob | undefined = await db.get('images', key);

  if (!blob) return null;

  // Create temporary objectURL
  const url = URL.createObjectURL(blob);
  activeObjectURLs.set(key, url);

  // Touch LRU metadata
  await db.put(
    'meta',
    {
      size: blob.size,
      lastUsed: Date.now(),
    },
    key
  );

  return url;
};

// LRU SET
const addToCache = async (key: string, blob: Blob) => {
  const db = await dbPromise;

  // Save blob to disk
  await db.put('images', blob, key);

  // Save metadata
  await db.put(
    'meta',
    {
      size: blob.size,
      lastUsed: Date.now(),
    },
    key
  );

  await enforceLRULimit();
};

// LRU EVICTION
const enforceLRULimit = async () => {
  const db = await dbPromise;
  const metaStore = db.transaction('meta', 'readonly').objectStore('meta');

  const entries: { key: string; size: number; lastUsed: number }[] = [];

  for (
    let cursor = await metaStore.openCursor();
    cursor;
    cursor = await cursor.continue()
  ) {
    entries.push({
      key: String(cursor.key),
      size: cursor.value.size,
      lastUsed: cursor.value.lastUsed,
    });
  }

  let totalBytes = entries.reduce((sum, e) => sum + e.size, 0);

  // Evict oldest until under limit
  entries.sort((a, b) => a.lastUsed - b.lastUsed);

  const tx = db.transaction(['images', 'meta'], 'readwrite');

  while (totalBytes > MAX_CACHE_BYTES && entries.length) {
    const lru = entries.shift()!;
    totalBytes -= lru.size;

    await tx.objectStore('images').delete(lru.key);
    await tx.objectStore('meta').delete(lru.key);

    revokeObjectURL(lru.key);
  }

  await tx.done;
};

// OBJECT URL CLEANUP
const revokeObjectURL = (key: string) => {
  const url = activeObjectURLs.get(key);
  if (url) {
    URL.revokeObjectURL(url);
    activeObjectURLs.delete(key);
  }
};

// CLEANUP
export const clearImageCache = async () => {
  const db = await dbPromise;

  activeObjectURLs.forEach((url) => URL.revokeObjectURL(url));
  activeObjectURLs.clear();

  await db.clear('images');
  await db.clear('meta');
};

// Remove unsafe beforeunload cleanup
// ❌ window.addEventListener('beforeunload', clearImageCache);

// PUBLIC FETCHER
export const loadImageBlob = async (
  cacheKey: string,
  presignedUrl: string
): Promise<string> => {
  try {
    // ✅ Disk + memory cache hit
    const cached = await getFromCache(cacheKey);
    if (cached) return cached;

    // ❌ Cache miss -> network fetch
    const res = await fetch(presignedUrl);
    if (!res.ok) {
      console.error('Image fetch failed:', res.status, res.statusText);
      return '';
    }

    const blob = await res.blob();

    // Persist Blob to disk (safe)
    await addToCache(cacheKey, blob);

    const url = URL.createObjectURL(blob);
    activeObjectURLs.set(cacheKey, url);

    return url;
  } catch (err) {
    console.error('loadImageBlob error:', err);
    return '';
  }
};

// Compat GET API
export const getCachedImageBlob = (key: string): string | null => {
  return activeObjectURLs.get(key) ?? null;
};
